import { Typography } from "@mui/material";
import { NotificationWhenControl } from "generated-graphql/graphql";

/**
 * The "When" column for one preference row: the timing the alert will actually
 * fire at, as text.
 *
 * DELIBERATELY READ-ONLY, and not because the controls were left for later.
 * Timing is applied where a notification is *emitted*, and emission today asks
 * one question for the whole tenant ("is anything due inside N days") rather than
 * once per recipient. A day input here would therefore save a number the platform
 * would never honour: the reader would be told it worked and see no change, which
 * is worse than not offering the control.
 *
 * Why the explanation is not here: it is a fact about the column, identical on
 * every row, and it used to be a per-cell tooltip on a bare `<span>` — which put
 * it out of reach of the keyboard entirely and hung an `aria-label` on a `generic`
 * element that assistive technology discards. A read-only column with no reachable
 * reason reads as a broken control rather than as a considered decision, so the
 * reason now lives once in `AlertSectionAccordion`'s column header: a short
 * caption that is visible with no interaction at all, plus a focusable info button
 * carrying the full sentence.
 *
 * The seam for when that changes: once emission fans out per recipient (or the
 * platform re-fans a notification per distinct lead time), replace the text below
 * with the bounded day input for DAYS_BEFORE and the coarse percent select for
 * PERCENT_THRESHOLD, and include `leadTimeDays` / `thresholdPercent` in the save
 * payload. The write path already validates and stores both — a lead time of 1 to
 * 90 days, a threshold from a fixed set — so the change is this component plus
 * `buildSavePayload`, and nothing else on the page has to move.
 */

/**
 * What the column says when it has nothing to say: an unset fixed label, or a
 * control whose value failed to resolve.
 *
 * The absent case is deliberately not defaulted to a number. A lead time of 0 is
 * not a legal stored value — the write path's floor is 1 — and "At 0%" is a
 * threshold no alert has, so both would render as a confident statement about
 * timing that is simply false, and a reader has no way to tell such a row from a
 * correctly-resolved one. An em dash is the vocabulary this column already uses
 * for "the platform is not saying", which is the truth in that case.
 */
const NO_TIMING = "—";

/**
 * Fixed labels that say only "this fires when the thing happens", which the
 * notification's own name already tells the reader. Matched as data rather than
 * guessed from the string so a label with real content ("At the deadline") is
 * never swallowed.
 */
const EVENT_DRIVEN_LABELS = new Set([
  "When it happens",
  "When it finishes",
  "When it is sent",
  "When it is published",
  NO_TIMING,
]);

export type WhenCellProps = {
  when: {
    control: NotificationWhenControl;
    fixedLabel?: string | null;
    leadTimeDays?: number | null;
    thresholdPercent?: number | null;
  };
};

function whenText(when: WhenCellProps["when"]): string {
  if (when.control === NotificationWhenControl.DaysBefore) {
    const days = when.leadTimeDays;
    if (days === null || days === undefined) {
      return NO_TIMING;
    }
    // A lead time of 1 is legal — it is the floor the write path enforces — so the
    // singular is a case this column really renders, not a hypothetical.
    return `${days} ${days === 1 ? "day" : "days"} before`;
  }
  if (when.control === NotificationWhenControl.PercentThreshold) {
    const percent = when.thresholdPercent;
    if (percent === null || percent === undefined) {
      return NO_TIMING;
    }
    // Lowercase per the mockup: the cell reads as the tail of a sentence about the
    // alert ("... fires at 90%"), not as a heading.
    return `at ${percent}%`;
  }
  // Event-driven kinds return empty rather than their fixed label. "When it
  // happens" beside a name that already says so is the repetition that made the
  // column worth removing; the label survives for the cases that carry real
  // information, like "At the deadline".
  const fixed = when.fixedLabel ?? "";
  return EVENT_DRIVEN_LABELS.has(fixed) ? "" : fixed;
}

/**
 * Rendered inline beside the notification's name rather than in a column.
 *
 * Most notifications fire on an event and have nothing to tune, so a column of
 * its own spent a fifth of the table's width repeating "when it happens". Inline,
 * the handful with a real number read as a qualifier on the name — "Container
 * deadline approaching, 30 days before" — and the rest say nothing at all.
 *
 * Deliberately not a select. The value cannot be honoured at emission time yet,
 * so a caret here would offer an edit that saves and changes nothing. When
 * per-recipient timing lands this becomes the bounded day input and the coarse
 * percent select, and `buildSavePayload` starts carrying the two fields.
 */
export function WhenCell({ when }: WhenCellProps) {
  const text = whenText(when);
  if (!text) {
    return null;
  }

  return (
    <Typography variant="body2" color="text.secondary" component="span">
      {text}
    </Typography>
  );
}
